import { useState, useEffect } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Settings as SettingsIcon, KeyRound, DatabaseBackup, Calendar, Save } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [fy, setFy] = useState("");
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });

  useEffect(() => {
    if (!user?.panchayat_id) return;
    api.get(`/settings/panchayat/${user.panchayat_id}`).then((r) => {
      setSettings(r.data);
      setFy(r.data.current_financial_year || "2025-26");
    });
  }, [user]);

  const saveFy = async () => {
    try {
      await api.patch("/settings/panchayat", { panchayat_id: user.panchayat_id, current_financial_year: fy });
      toast.success("Financial year updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const changePassword = async () => {
    if (pwd.new_password !== pwd.confirm) { toast.error("Passwords do not match"); return; }
    try {
      await api.post("/auth/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Password changed");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const backup = () => {
    const token = localStorage.getItem("hsps_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/backup/json${user?.panchayat_id ? `?panchayat_id=${user.panchayat_id}` : ""}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const u = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = u; a.download = `hsps_backup_${Date.now()}.json`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        toast.success("Backup downloaded");
      })
      .catch(() => toast.error("Backup failed"));
  };

  return (
    <OfficerLayout>
      <div>
        <div className="mb-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">Options</div>
          <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
            <SettingsIcon className="w-7 h-7 text-[#0A3161]" />
            {lang === "hi" ? "सेटिंग्स एवं विकल्प" : "Settings & Options"}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Financial Year */}
          <div className="rounded-md border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-3"><Calendar className="w-5 h-5 text-[#0A3161]" /><h3 className="font-heading font-bold">{lang === "hi" ? "वित्तीय वर्ष" : "Financial Year"}</h3></div>
            <p className="text-xs text-slate-600">{lang === "hi" ? "वर्तमान वित्तीय वर्ष सेट करें। डेटा वर्ष-वार पृथक रहता है।" : "Set the active financial year. Data remains separated year-wise."}</p>
            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Current FY</label>
              <input value={fy} onChange={(e) => setFy(e.target.value)} placeholder="2025-26"
                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-mono" data-testid="fy-input" />
              <button onClick={saveFy} data-testid="fy-save" className="mt-3 w-full px-3 py-2 text-xs font-semibold rounded-md text-white inline-flex items-center justify-center gap-1.5" style={{ background: "#0A3161" }}>
                <Save className="w-3.5 h-3.5" /> {lang === "hi" ? "सहेजें" : "Save"}
              </button>
            </div>
          </div>

          {/* Change Password */}
          <div className="rounded-md border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-3"><KeyRound className="w-5 h-5 text-[#0A3161]" /><h3 className="font-heading font-bold">{lang === "hi" ? "पासवर्ड बदलें" : "Change Password"}</h3></div>
            <div className="space-y-3">
              {[
                { k: "current_password", lbl: lang === "hi" ? "पुराना पासवर्ड" : "Current Password", tid: "pwd-current" },
                { k: "new_password", lbl: lang === "hi" ? "नया पासवर्ड" : "New Password", tid: "pwd-new" },
                { k: "confirm", lbl: lang === "hi" ? "पुष्टि करें" : "Confirm", tid: "pwd-confirm" },
              ].map((f) => (
                <div key={f.k}>
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{f.lbl}</label>
                  <input type="password" value={pwd[f.k]} onChange={(e) => setPwd({ ...pwd, [f.k]: e.target.value })}
                    data-testid={f.tid}
                    className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm" />
                </div>
              ))}
              <button onClick={changePassword} data-testid="pwd-submit" className="w-full px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
                {lang === "hi" ? "पासवर्ड बदलें" : "Update Password"}
              </button>
            </div>
          </div>

          {/* Backup */}
          <div className="rounded-md border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 mb-3"><DatabaseBackup className="w-5 h-5 text-[#0A3161]" /><h3 className="font-heading font-bold">{lang === "hi" ? "बैकअप" : "Backup"}</h3></div>
            <p className="text-xs text-slate-600">{lang === "hi" ? "सम्पूर्ण डेटा JSON में डाउनलोड करें। ऑफ़लाइन सहेजें।" : "Download a JSON snapshot of all data. Save offline securely."}</p>
            <button onClick={backup} data-testid="backup-btn"
              className="mt-5 w-full px-3 py-2 text-xs font-semibold rounded-md text-white inline-flex items-center justify-center gap-1.5" style={{ background: "#E87722" }}>
              <DatabaseBackup className="w-3.5 h-3.5" /> {lang === "hi" ? "बैकअप डाउनलोड करें" : "Download Backup (JSON)"}
            </button>
          </div>
        </div>

        <div className="mt-6 rounded-md border border-slate-200 bg-white p-5" data-testid="master-formats">
          <div className="flex items-center gap-2 mb-3"><SettingsIcon className="w-5 h-5 text-[#0A3161]" /><h3 className="font-heading font-bold">{lang === "hi" ? "मास्टर फॉर्मेट" : "Master Format Customization"}</h3></div>
          <p className="text-xs text-slate-600">{lang === "hi" ? "आकलन, रसीद, मांग नोटिस एवं रोजमेल के लिए कस्टम हेडर / फूटर टेक्स्ट सेट करें।" : "Custom header / footer text for Assessment, Receipt, Demand Notice and Rozmel printable formats."}</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { k: "assessment", lbl: lang === "hi" ? "आकलन फॉर्मेट" : "Assessment Format" },
              { k: "receipt", lbl: lang === "hi" ? "रसीद फॉर्मेट" : "Receipt Format" },
              { k: "demand_notice", lbl: lang === "hi" ? "मांग नोटिस फॉर्मेट" : "Demand Notice Format" },
              { k: "rozmel", lbl: lang === "hi" ? "रोजमेल फॉर्मेट" : "Rozmel Format" },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{f.lbl}</label>
                <textarea rows={3} placeholder="Custom header / footer text"
                  value={(settings?.formats?.[f.k]) || ""}
                  data-testid={`fmt-${f.k}`}
                  onChange={(e) => setSettings({ ...settings, formats: { ...(settings?.formats || {}), [f.k]: e.target.value } })}
                  className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-mono" />
              </div>
            ))}
          </div>
          <button
            onClick={async () => {
              try {
                await api.patch("/settings/panchayat", { panchayat_id: user.panchayat_id, formats: settings?.formats || {} });
                toast.success("Formats saved");
              } catch (e) { toast.error("Failed"); }
            }}
            data-testid="fmt-save"
            className="mt-4 px-4 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
            {lang === "hi" ? "फॉर्मेट सहेजें" : "Save Formats"}
          </button>
        </div>
      </div>
    </OfficerLayout>
  );
}
